import {Injectable, signal, Signal} from '@angular/core';

export type PantryTheme = 'light' | 'dark';
type ThemeMode = PantryTheme | 'system';

const STORAGE_KEY = 'pantry.theme';

@Injectable({providedIn: 'root'})
export class ThemeService {

  private currentTheme: PantryTheme | null = null;
  private mediaQuery: MediaQueryList | null = null;

  // Keep a reference so we can remove listeners if init() is called twice.
  private onMediaQueryChange: ((ev?: MediaQueryListEvent) => void) | null = null;

  private readonly _theme = signal<PantryTheme>('light');

  /** Reactive signal for the currently active theme ('light' | 'dark'). */
  readonly theme: Signal<PantryTheme> = this._theme.asReadonly();

  /**
   * Initialize theme on app start.
   * Default behavior: follow OS preference and react to changes live.
   * If user explicitly saves 'light' or 'dark', that overrides OS.
   */
  init(): void {
    if (typeof window === 'undefined') {
      // Non-browser environments: just make sure we have a deterministic value.
      this.applyTheme('light');
      return;
    }

    // Ensure matchMedia is available.
    this.mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)') ?? null;

    // Apply once immediately.
    this.applyCurrentMode();

    // Listen for OS changes.
    if (this.mediaQuery) {
      // Remove old listeners defensively.
      if (this.onMediaQueryChange) {
        try {
          this.mediaQuery.removeEventListener('change', this.onMediaQueryChange);
        } catch {
          // Safari <= 13
          this.mediaQuery.removeListener?.(this.onMediaQueryChange);
        }
      }

      this.onMediaQueryChange = () => {
        // Only auto-follow OS if mode is system.
        if (this.getMode() === 'system') {
          this.applyTheme(this.getSystemTheme());
        }
      };

      // Modern browsers.
      try {
        this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
      } catch {
        // Safari <= 13
        this.mediaQuery.addListener?.(this.onMediaQueryChange);
      }
    }
  }

  /** The *effective* theme currently applied (light/dark). */
  getTheme(): PantryTheme {
    if (this.currentTheme) return this.currentTheme;

    const attr = this.getHtmlThemeAttr();
    if (attr === 'light' || attr === 'dark') {
      this.currentTheme = attr;
      return attr;
    }

    // Fall back to the current mode resolution.
    const resolved = this.resolveModeToTheme(this.getMode());
    this.currentTheme = resolved;
    return resolved;
  }

  /** Returns the saved mode: 'system' | 'light' | 'dark'. */
  getMode(): ThemeMode {
    if (typeof window === 'undefined') return 'system';

    const raw = window.localStorage.getItem(STORAGE_KEY);

    // Backwards compatibility (old values)
    if (raw === 'pantry-light') return 'light';
    if (raw === 'pantry-dark') return 'dark';

    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;

    // Default: system.
    return 'system';
  }

  /**
   * Explicitly set mode.
   * - 'system' => follow OS and update live
   * - 'light'/'dark' => fixed
   */
  setMode(mode: ThemeMode): void {
    this.saveMode(mode);
    this.applyCurrentMode();
  }

  /** Backwards-compatible API: forces light/dark and stores it as an override. */
  setTheme(theme: PantryTheme): void {
    this.setMode(theme);
  }

  /** Convenience for a light/dark checkbox toggle. */
  setDarkModeEnabled(enabled: boolean): void {
    this.setMode(enabled ? 'dark' : 'light');
  }

  private applyCurrentMode(): void {
    this.applyTheme(this.resolveModeToTheme(this.getMode()));
  }

  private resolveModeToTheme(mode: ThemeMode): PantryTheme {
    if (mode === 'light' || mode === 'dark') return mode;
    return this.getSystemTheme();
  }

  private applyTheme(theme: PantryTheme): void {
    this.currentTheme = theme;
    this._theme.set(theme);

    if (typeof document === 'undefined') return;

    // DaisyUI reads theme from the root element's data-theme attribute.
    document.documentElement.setAttribute('data-theme', theme);
  }

  private saveMode(mode: ThemeMode): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, mode);
  }

  private getSystemTheme(): PantryTheme {
    if (typeof window === 'undefined') return 'light';

    const mq = this.mediaQuery ?? window.matchMedia?.('(prefers-color-scheme: dark)');
    return mq?.matches ? 'dark' : 'light';
  }

  private getHtmlThemeAttr(): string | null {
    if (typeof document === 'undefined') return null;
    return document.documentElement.getAttribute('data-theme');
  }
}
